// ─── Bounds and opening values for the ops settings controls ────────
// Shared by the cards so the inputs agree with each other and with the stored
// schema. These mirror the bounds `AdminOpsSettingsSchema` enforces in
// @nexus/db — a field the form allows but the server rejects is a save that
// fails with a validation error nobody can act on.

export const LIMITS = {
  eventsRetentionDays: { min: 7, max: 365 },
  eventsStuckAfterMinutes: { min: 5, max: 24 * 60 },
  failedEventsAlertThreshold: { min: 1, max: 1000 },
  slowActionSecondsDefault: { min: 30, max: 24 * 60 * 60 },
  jobsOverdueGraceMinutes: { min: 1, max: 12 * 60 },
  jobsUnhealthyAfterFailures: { min: 1, max: 10 },
  jobsAutoRefreshSeconds: { min: 15, max: 60 * 60 },
  tokenUsageRetentionDays: { min: 7, max: 365 },
  tokenUsageAutoRefreshSeconds: { min: 15, max: 60 * 60 },
  tokenUsageLongTailPercent: { min: 1, max: 25 },
  tokenSpendAlertThreshold: { min: 1_000, max: 2_000_000_000 },
  tenantTokenSpendAlertThreshold: { min: 1_000, max: 2_000_000_000 },
  tokenSpendAlertCooldownHours: { min: 1, max: 24 * 7 },
  llmLatencyAlertP95Seconds: { min: 1, max: 10 * 60 },
  monthlyBudgetUsd: { min: 1, max: 100_000 },
  budgetAlertAtPercent: { min: 10, max: 99 },
  /**
   * The rate dial is in cents per TB; the column is in micro-dollars. The
   * ceiling mirrors `MAX_BIGQUERY_PRICE_PER_TB_MICROS`, which is derived from
   * KTD16's arithmetic bound and not from plausibility — read the comment on it
   * in `z.admin-ops-settings.ts` before raising this.
   */
  bigQueryPricePerTbCents: { min: 1, max: 100_000 },
  /** No zero position, unlike the retentions above: R31 bounds this table. */
  bigQueryRetentionDays: { min: 7, max: 365 },
  /** Both byte thresholds, in GiB: one gibibyte to one pebibyte. */
  bigQueryBytesAlertThresholdGib: { min: 1, max: 1_048_576 },
  bigQueryBytesAlertCooldownHours: { min: 1, max: 24 * 7 },
} as const;

/** The rate is stored per micro-dollar and typed in cents. */
export const MICROS_PER_CENT = 10_000;

/**
 * Both byte dials are in GiB. Bytes is the unit the thresholds are stored and
 * compared in (R35), but a field holding 10995116277760 cannot be read, and GiB
 * is the largest unit that still covers the gibibyte floor as a whole number.
 */
export const BYTES_PER_GIB = 1024 ** 3;

/** The windows the spend alert may total over; mirrors the stored schema. */
export const SPEND_ALERT_WINDOWS = [1, 6, 24] as const;

/** What the usage chart can stack by; mirrors `TOKEN_USAGE_GROUPINGS`. */
export const GROUPINGS = ["model", "feature", "tenant"] as const;

/** The latency dial is in seconds; the column and the alert are in milliseconds. */
export const MS_PER_SECOND = 1_000;

/** What the budget reads when it is first switched on: $500 a month. */
export const BUDGET_DEFAULT_CENTS = 50_000;

/** What the retention dial reads when the switch is first turned on. */
export const RETENTION_DEFAULT_DAYS = 30;

/** What the auto-refresh dial reads when the switch is first turned on. */
export const AUTO_REFRESH_DEFAULT_SECONDS = 60;
